import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

/**
 * A modern, stable, and beautiful Tag Cloud / Word Cloud replacement.
 * This component avoids complex D3 layouts that crash in React 19
 * and provides a clean MD3-styled visual experience.
 */
const ModernWordCloud = ({ words = [], colors = [] }) => {
    // Transform and shuffle words for a "cloud" look
    const cloudWords = useMemo(() => {
        if (!Array.isArray(words)) return [];
        
        return words.map((word, index) => {
            // Deterministic but random-looking properties
            const fontSize = Math.floor(Math.random() * 24) + 14; // 14px to 38px
            const fontWeight = fontSize > 28 ? '700' : fontSize > 20 ? '600' : '500';
            const opacity = (fontSize / 40) * 0.5 + 0.5; // Larger words are more opaque
            const color = colors.length > 0 ? colors[index % colors.length] : 'currentColor';
            const rotation = (Math.random() > 0.8) ? (Math.random() > 0.5 ? 90 : -90) : 0;
            
            return {
                text: typeof word === 'string' ? word : (word.text || ''),
                fontSize,
                fontWeight,
                opacity,
                color,
                rotation,
                id: `${word}-${index}`
            };
        }).sort(() => Math.random() - 0.5); // Shuffle for visual variety
    }, [words, colors]);

    if (cloudWords.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-surface-onVariant/40 italic text-sm">
                Keine Wörter vorhanden
            </div>
        );
    }

    return (
        <div className="relative w-full h-full flex flex-wrap items-center justify-center gap-x-4 gap-y-2 p-4 overflow-hidden">
            {cloudWords.map((word, index) => (
                <motion.span
                    key={word.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: word.opacity }}
                    transition={{ 
                        type: 'spring', 
                        stiffness: 100, 
                        delay: index * 0.03,
                        duration: 0.5 
                    }}
                    whileHover={{ 
                        scale: 1.15, 
                        opacity: 1,
                        transition: { duration: 0.2 } 
                    }}
                    style={{
                        fontSize: `${word.fontSize}px`,
                        fontWeight: word.fontWeight,
                        color: word.color,
                        transform: `rotate(${word.rotation}deg)`,
                        display: 'inline-block',
                        cursor: 'default',
                        userSelect: 'none',
                        textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                    className="transition-colors hover:text-primary"
                >
                    {word.text}
                </motion.span>
            ))}
        </div>
    );
};

export default ModernWordCloud;
